import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, FileText, MoreHorizontal, DollarSign, Send } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';

const InvoicesPage = () => {
  const { token, API } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [formData, setFormData] = useState({
    customer_id: '',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    due_date: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [invoicesRes, customersRes] = await Promise.all([
        axios.get(`${API}/invoices${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setInvoices(invoicesRes.data.invoices);
      setCustomers(customersRes.data.customers);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || !formData.due_date) {
      toast.error('Customer and due date are required');
      return;
    }

    try {
      await axios.post(`${API}/invoices`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invoice ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      await axios.post(`${API}/invoices/${selectedInvoice.id}/record-payment?amount=${paymentAmount}&method=cash`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment recorded');
      setPaymentDialogOpen(false);
      setSelectedInvoice(null);
      setPaymentAmount('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentAmount((invoice.total - (invoice.paid_amount || 0)).toString());
    setPaymentDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      line_items: [{ description: '', quantity: 1, unit_price: 0 }],
      due_date: '',
      notes: ''
    });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...formData.line_items];
    items[index][field] = value;
    setFormData({ ...formData, line_items: items });
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'Unknown';

  const filteredInvoices = invoices.filter(inv =>
    getCustomerName(inv.customer_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const config = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-emerald-100 text-emerald-800',
      partially_paid: 'bg-amber-100 text-amber-800',
      overdue: 'bg-red-100 text-red-800'
    };
    return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config[status] || config.draft}`}>{status.replace('_', ' ')}</span>;
  };

  return (
    <ClientLayout title="Invoices">
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-4 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search invoices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-invoices-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="partially_paid">Partial</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-invoice-btn">
                  <Plus className="h-4 w-4 mr-2" /> New Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create New Invoice</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Customer *</Label>
                      <Select 
                        value={formData.customer_id} 
                        onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                      >
                        <SelectTrigger data-testid="invoice-customer-select">
                          <SelectValue placeholder="Select customer" />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((customer) => (
                            <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Due Date *</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                        data-testid="invoice-due-date-input"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Line Items</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                        <Plus className="h-4 w-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.line_items.map((item, index) => (
                        <div key={index} className="flex gap-2 items-start">
                          <Input
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            className="flex-1"
                          />
                          <Input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                            className="w-20"
                          />
                          <Input
                            type="number"
                            placeholder="Price"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-24"
                          />
                          <span className="w-20 text-right py-2 text-sm font-medium">
                            ${(item.quantity * item.unit_price).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end mt-2 pt-2 border-t">
                      <span className="font-semibold">Total: ${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSubmit} data-testid="save-invoice-btn">
                    Create Invoice
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No invoices found</p>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                Create Your First Invoice
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Invoice #</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Customer</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Total</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Paid</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Due Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`invoice-${invoice.id}`}>
                      <td className="py-3 px-4 font-medium text-gray-900">{invoice.invoice_number}</td>
                      <td className="py-3 px-4 text-gray-600">{getCustomerName(invoice.customer_id)}</td>
                      <td className="py-3 px-4 font-medium text-gray-900">${invoice.total?.toLocaleString()}</td>
                      <td className="py-3 px-4 text-gray-600">${(invoice.paid_amount || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-gray-600 text-sm">{invoice.due_date}</td>
                      <td className="py-3 px-4">{getStatusBadge(invoice.status)}</td>
                      <td className="py-3 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {invoice.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(invoice.id, 'sent')}>
                                <Send className="h-4 w-4 mr-2" /> Send Invoice
                              </DropdownMenuItem>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'partially_paid') && (
                              <DropdownMenuItem onClick={() => openPaymentDialog(invoice)}>
                                <DollarSign className="h-4 w-4 mr-2" /> Record Payment
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedInvoice && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Invoice: {selectedInvoice.invoice_number}</p>
                <p className="text-sm text-gray-500">Total: ${selectedInvoice.total}</p>
                <p className="text-sm text-gray-500">Balance Due: ${(selectedInvoice.total - (selectedInvoice.paid_amount || 0)).toFixed(2)}</p>
              </div>
            )}
            <div>
              <Label>Payment Amount ($)</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                data-testid="payment-amount-input"
              />
            </div>
            <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleRecordPayment} data-testid="record-payment-btn">
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ClientLayout>
  );
};

export default InvoicesPage;
